/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import {
    ApplicationRef,
    ChangeDetectorRef,
    Component,
    ComponentFactoryResolver,
    ComponentRef,
    HostListener,
    Injector,
    Input,
    OnDestroy,
    SimpleChange,
    SimpleChanges,
} from '@angular/core';
import { ReportDefinition } from '../../../state/report.model';
import { Observable, Subject } from 'rxjs';
import { ComponentPortal, DomPortalOutlet } from '@angular/cdk/portal';
import { concatMapTo, filter, map, take, takeUntil, tap } from 'rxjs/operators';
import { WindowRefService } from '../../../../../core/ui/services/windowRef.service';
import { ReportContentContainerPrintComponent } from '../../../reportContent/report-content-container-print.component';
import { ReportState } from '../../../state/report.state';
import { Store } from '@ngxs/store';
import { IsolatedReportService } from '../../../services/isolatedReport.service';
import { AppConfigState } from '../../../../../core/state/appConfig/app-config.state';

@Component({
    selector: 'dash-gis-report-print',
    templateUrl: './report-print.component.html',
    styleUrls: ['./report-print.component.scss'],
})
export class ReportPrintComponent implements OnDestroy {
    @Input() definition: ReportDefinition;

    private portalOutlet: DomPortalOutlet;
    private printContent: ComponentRef<ReportContentContainerPrintComponent>;
    private stopSubject = new Subject<void>();
    private stop$: Observable<void>;

    constructor(
        private isolatedReport: IsolatedReportService,
        private componentFactoryResolver: ComponentFactoryResolver,
        private appRef: ApplicationRef,
        private injector: Injector,
        private cdr: ChangeDetectorRef,
        private windowRef: WindowRefService,
        private store: Store
    ) {
        this.stop$ = this.stopSubject.asObservable();
    }

    ngOnDestroy() {
        this.stopSubject.next();
        if (this.portalOutlet) {
            this.portalOutlet.detach();
        }
    }

    @HostListener('window:afterprint')
    afterPrint() {
        this.stopSubject.next();
        if (this.portalOutlet) {
            this.portalOutlet.detach();
        }
    }

    print() {
        const printDelay = this.store.selectSnapshot(AppConfigState.printDelay);
        if (this.portalOutlet && this.portalOutlet.hasAttached()) {
            this.portalOutlet.detach();
        }
        this.createPrintPortal();
        this.printContent.instance.initialized$
            .pipe(
                filter((b) => b),
                tap(() => this.isolatedReport.triggerAllData()),
                concatMapTo(this.printContent.instance.dataSource.loading$),
                filter((loading) => !loading),
                concatMapTo(this.printContent.instance.dataSource.reportAllData$),
                filter((data) => data !== null),
                take(1),
            )
            .subscribe(() => {
                setTimeout(() => {
                    this.windowRef.nativeWindow.print();
                }, printDelay);
            });
    }

    createPrintPortal() {
        const portal = new ComponentPortal(ReportContentContainerPrintComponent, null, this.injector);
        this.portalOutlet = new DomPortalOutlet(
            document.querySelector('#printContent'),
            this.componentFactoryResolver,
            this.appRef,
            this.injector
        );
        this.printContent = this.portalOutlet.attachComponentPortal<ReportContentContainerPrintComponent>(portal);
        this.keys$.pipe(takeUntil(this.stop$)).subscribe((keys) => {
            const changes: SimpleChanges = {};

            const keysChange = this.buildChange(this.printContent.instance.keys, keys);
            this.printContent.instance.keys = keys;
            changes['keys'] = keysChange;

            const definitionChange = this.buildChange(this.printContent.instance.definition, this.definition);
            this.printContent.instance.definition = this.definition;
            changes['definition'] = definitionChange;

            const printChange = this.buildChange(this.printContent.instance.print, true);
            this.printContent.instance.print = true;
            changes['print'] = printChange;

            this.printContent.instance.ngOnChanges(changes);
            this.cdr.markForCheck();
        });
    }

    private buildChange(previous, current): SimpleChange {
        let firstChange = false;
        if (typeof (previous === 'undefined')) {
            firstChange = true;
        }
        return new SimpleChange(previous, current, firstChange);
    }

    get keys$(): Observable<any[]> {
        return this.store.select(ReportState.featureReportKeys).pipe(map((fn) => fn(this.definition.layer)));
    }
}
